import React, { useEffect, useMemo, useState } from "react";
import { Select } from "antd";
import { useNotification } from "../../NotificationContext";
import { createEpisode } from "../../apis/episode";
import { getEpisodeTypes } from "../../apis/episodeType";
import { getClinicUsers } from "../../apis/user";
import {
  calculateGestationalAge,
  calculateEDDFromLMP,
  formatDate,
  parseGplad,
  GpladValue,
} from "../../utils/EpisodeMeta";

interface CreateEpisodeModalProps {
  patientId: number;
  patientName?: string;
  patientGender?: string;
  onClose: () => void;
  onCreated?: () => void;
}

const HISTORY_TABLE_COLUMNS = [
  { key: "year",            label: "Year",          type: "number", width: "w-16" },
  { key: "ga_weeks",        label: "GA (wks)",       type: "number", width: "w-16" },
  { key: "mode",            label: "Mode",           type: "select", width: "w-24",
    options: ["Vaginal", "LSCS", "Forceps", "Vacuum", "Other"] },
  { key: "birth_weight",    label: "Wt (kg)",        type: "text",   width: "w-16" },
  { key: "sex",             label: "Sex",            type: "select", width: "w-16",
    options: ["M", "F"] },
  { key: "outcome",         label: "Outcome",        type: "select", width: "w-24",
    options: ["Live birth", "Stillbirth", "Abortion", "Ectopic", "IUFD"] },
  { key: "complications",   label: "Complications",  type: "text",   width: "w-24" },
  { key: "lscs_indication", label: "LSCS Indication", type: "text", width: "w-28" },
] as const;

const emptyHistoryRow = () =>
  Object.fromEntries(HISTORY_TABLE_COLUMNS.map((c) => [c.key, ""]));

const CreateEpisodeModal: React.FC<CreateEpisodeModalProps> = ({
  patientId,
  patientName,
  patientGender,
  onClose,
  onCreated,
}) => {
  const { showNotification } = useNotification();
  const [episodeTypes, setEpisodeTypes] = useState<any[]>([]);
  const [doctors, setDoctors] = useState<any[]>([]);
  const [selectedTypeId, setSelectedTypeId] = useState<number | null>(null);
  const [assignedUserId, setAssignedUserId] = useState<number | null>(null);
  const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [meta, setMeta] = useState<Record<string, any>>({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    getEpisodeTypes({ per_page: 100 }).then((res: any) => {
      setEpisodeTypes(res.data?.data || res.data || []);
    });
    getClinicUsers().then((res: any) => {
      const list: any[] = res?.data || [];
      setDoctors(list.filter((u: any) => Array.isArray(u.roles) && u.roles.includes("doctor")));
    });
  }, []);

  const availableEpisodeTypes = useMemo(
    () => episodeTypes.filter((t) =>
      !t.genderRestriction || t.genderRestriction === patientGender
    ),
    [episodeTypes, patientGender]
  );

  const selectedType = useMemo(
    () => episodeTypes.find((t) => t.id === selectedTypeId),
    [episodeTypes, selectedTypeId]
  );

  const metaFields: any[] = useMemo(() => {
    if (!selectedType) return [];
    if (Array.isArray(selectedType.fields) && selectedType.fields.length > 0)
      return selectedType.fields;
    if (Array.isArray(selectedType.metaFields)) return selectedType.metaFields;
    if (Array.isArray(selectedType.meta?.fields)) return selectedType.meta.fields;
    if (Array.isArray(selectedType.meta)) return selectedType.meta;
    return [];
  }, [selectedType]);

  useEffect(() => {
    const defaults: Record<string, any> = {};
    metaFields.forEach((field: any) => {
      if (field.key) defaults[field.key] = field.defaultValue ?? field.default ?? "";
    });
    setMeta(defaults);
  }, [selectedTypeId, metaFields.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateMetaField = (key: string, value: any) =>
    setMeta((prev) => ({ ...prev, [key]: value }));

  // ─── EDD field helpers ───────────────────────────────────────────────────────

  const renderEddField = (field: any) => {
    const key: string = field.key;
    const raw: any = meta[key] ?? {};
    const usgEdd: string = raw.usg_edd ?? "";
    const active: string = raw.active ?? "lmp";

    const lmpKey = metaFields.find((f: any) => f.type === "lmp")?.key ?? "lmp";
    const lmpDate: string = meta[lmpKey] ?? "";
    const lmpEdd = formatDate(calculateEDDFromLMP(lmpDate));

    const patch = (changes: Partial<{ usg_edd: string; active: string }>) =>
      updateMetaField(key, { ...raw, ...changes });

    return (
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">{field.label}</label>
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-2">
          {/* LMP-derived EDD */}
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name={`edd_active_${key}`}
                checked={active === "lmp"}
                onChange={() => patch({ active: "lmp" })}
                className="text-indigo-600"
              />
              <span className="text-xs font-medium text-gray-600">LMP-based EDD</span>
            </label>
            <span className={`text-sm font-mono ${lmpEdd ? "text-gray-800" : "text-gray-400"}`}>
              {lmpEdd || (lmpDate ? "calculating…" : "— enter LMP above —")}
            </span>
            {active === "lmp" && lmpEdd && (
              <span className="inline-flex items-center rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-semibold text-indigo-700 ring-1 ring-inset ring-indigo-200">
                Active
              </span>
            )}
          </div>
          {/* USG EDD override */}
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name={`edd_active_${key}`}
                checked={active === "usg"}
                onChange={() => patch({ active: "usg" })}
                className="text-indigo-600"
                disabled={!usgEdd}
              />
              <span className="text-xs font-medium text-gray-600">USG EDD</span>
            </label>
            <input
              type="date"
              value={usgEdd}
              onChange={(e) => {
                patch({ usg_edd: e.target.value, active: e.target.value ? "usg" : "lmp" });
              }}
              className="rounded-md border-gray-300 shadow-sm text-sm"
            />
            {active === "usg" && usgEdd && (
              <span className="inline-flex items-center rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-semibold text-indigo-700 ring-1 ring-inset ring-indigo-200">
                Active
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ─── History table field helpers ─────────────────────────────────────────────

  const renderHistoryTableField = (field: any) => {
    const key: string = field.key;
    const rows: any[] = Array.isArray(meta[key]) ? meta[key] : [];

    const updateRow = (rowIdx: number, colKey: string, value: string) => {
      const updated = rows.map((r, i) =>
        i === rowIdx ? { ...r, [colKey]: value } : r
      );
      updateMetaField(key, updated);
    };

    const addRow = () => updateMetaField(key, [...rows, emptyHistoryRow()]);

    const removeRow = (rowIdx: number) =>
      updateMetaField(key, rows.filter((_, i) => i !== rowIdx));

    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="block text-sm font-medium text-gray-700">{field.label}</label>
          <button
            type="button"
            onClick={addRow}
            className="flex items-center gap-1 px-2 py-1 text-xs font-semibold text-indigo-600 border border-indigo-300 rounded hover:bg-indigo-50"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" fill="none" viewBox="0 0 24 24" strokeWidth="2.5" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
            </svg>
            Add
          </button>
        </div>
        {rows.length === 0 ? (
          <p className="text-xs text-gray-400 py-2">No previous pregnancies recorded.</p>
        ) : (
          <div className="overflow-x-auto rounded border border-gray-200">
            <table className="min-w-full text-xs divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {HISTORY_TABLE_COLUMNS.map((c) => (
                    <th key={c.key} className="px-2 py-1.5 text-left font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      {c.label}
                    </th>
                  ))}
                  <th className="px-2 py-1.5" />
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {rows.map((row, rowIdx) => (
                  <tr key={rowIdx}>
                    {HISTORY_TABLE_COLUMNS.map((col) => (
                      <td key={col.key} className="px-1 py-1">
                        {col.type === "select" ? (
                          <select
                            value={row[col.key] ?? ""}
                            onChange={(e) => updateRow(rowIdx, col.key, e.target.value)}
                            className={`${col.width} rounded border border-gray-300 py-0.5 px-1 text-xs`}
                          >
                            <option value="" />
                            {(col as any).options.map((o: string) => (
                              <option key={o} value={o}>{o}</option>
                            ))}
                          </select>
                        ) : (
                          <input
                            type={col.type === "number" ? "number" : "text"}
                            value={row[col.key] ?? ""}
                            onChange={(e) => updateRow(rowIdx, col.key, e.target.value)}
                            className={`${col.width} rounded border border-gray-300 py-0.5 px-1 text-xs`}
                          />
                        )}
                      </td>
                    ))}
                    <td className="px-1 py-1">
                      <button
                        type="button"
                        onClick={() => removeRow(rowIdx)}
                        className="text-gray-300 hover:text-red-500"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  // ─── Generic field renderer ──────────────────────────────────────────────────

  const FULL_WIDTH_TYPES = new Set(["edd", "gplad", "history_table", "textarea"]);

  const renderField = (field: any) => {
    const key: string = field.key || field.name;
    const label: string = field.label || field.name || field.key;
    const type: string = field.type || "text";
    const value = meta[key] ?? "";
    if (!key) return null;

    const fullWidth = FULL_WIDTH_TYPES.has(type);
    const wrapClass = fullWidth ? "col-span-2" : "";

    if (type === "edd") return <div key={key} className={wrapClass}>{renderEddField(field)}</div>;
    if (type === "history_table") return <div key={key} className={wrapClass}>{renderHistoryTableField(field)}</div>;

    if (type === "textarea") {
      return (
        <div key={key} className={wrapClass}>
          <label className="block text-sm font-medium text-gray-700">
            {label}{field.required && <span className="text-red-500 ml-0.5">*</span>}
          </label>
          <textarea
            value={value}
            onChange={(e) => updateMetaField(key, e.target.value)}
            rows={3}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
          />
        </div>
      );
    }

    if (type === "select" && Array.isArray(field.options)) {
      return (
        <div key={key} className={wrapClass}>
          <label className="block text-sm font-medium text-gray-700">
            {label}{field.required && <span className="text-red-500 ml-0.5">*</span>}
          </label>
          <Select
            style={{ width: "100%" }}
            value={value || undefined}
            onChange={(val) => updateMetaField(key, val)}
            options={field.options.map((o: any) => ({ value: o.value ?? o, label: o.label ?? o }))}
          />
        </div>
      );
    }

    if (type === "boolean") {
      return (
        <div key={key} className={`flex items-center justify-between border border-gray-200 rounded-md px-3 py-2 ${wrapClass}`}>
          <label className="text-sm font-medium text-gray-700">
            {label}{field.required && <span className="text-red-500 ml-0.5">*</span>}
          </label>
          <button
            type="button"
            onClick={() => updateMetaField(key, !value)}
            className={`w-10 h-5 rounded-full transition-colors relative ${value ? "bg-indigo-600" : "bg-gray-300"}`}
          >
            <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${value ? "translate-x-5" : "translate-x-0.5"}`} />
          </button>
        </div>
      );
    }

    if (type === "lmp") {
      const ga = value ? calculateGestationalAge(value) : null;
      return (
        <div key={key} className={wrapClass}>
          <label className="block text-sm font-medium text-gray-700">
            {label}{field.required && <span className="text-red-500 ml-0.5">*</span>}
          </label>
          <div className="flex items-center gap-2 mt-1">
            <input
              type="date"
              value={value}
              onChange={(e) => updateMetaField(key, e.target.value)}
              className="block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
            />
            {ga && (
              <span className="shrink-0 inline-flex items-center rounded-full bg-rose-50 px-2.5 py-1 text-xs font-semibold text-rose-700 ring-1 ring-inset ring-rose-100">
                {ga.label} GA
              </span>
            )}
          </div>
        </div>
      );
    }

    if (type === "gplad") {
      const gplad: GpladValue =
        typeof value === "object" && value !== null ? value : parseGplad(value);
      const updateGplad = (part: keyof GpladValue, v: string) =>
        updateMetaField(key, { ...gplad, [part]: v === "" ? "" : Number(v) });
      const letters: Array<{ k: keyof GpladValue; title: string }> = [
        { k: "g", title: "Gravida" },
        { k: "p", title: "Para" },
        { k: "l", title: "Living" },
        { k: "a", title: "Abortus" },
        { k: "d", title: "Delivered" },
      ];
      return (
        <div key={key} className={wrapClass}>
          <label className="block text-sm font-medium text-gray-700">
            {label}{field.required && <span className="text-red-500 ml-0.5">*</span>}
          </label>
          <div className="mt-1 flex items-end gap-3">
            {letters.map(({ k, title }) => (
              <div key={k} className="flex flex-col items-center flex-1">
                <span className="text-[10px] font-semibold text-gray-400 uppercase mb-1">{title}</span>
                <input
                  type="number"
                  min={0}
                  value={gplad[k] === "" ? "" : gplad[k]}
                  onChange={(e) => updateGplad(k, e.target.value)}
                  placeholder={k.toUpperCase()}
                  className="w-full rounded-md border-gray-300 shadow-sm text-center sm:text-sm"
                />
              </div>
            ))}
          </div>
        </div>
      );
    }

    return (
      <div key={key} className={wrapClass}>
        <label className="block text-sm font-medium text-gray-700">
          {label}{field.required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
        <input
          type={type === "number" ? "number" : type === "date" ? "date" : "text"}
          value={value}
          onChange={(e) => updateMetaField(key, e.target.value)}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
        />
      </div>
    );
  };

  // ─── Save ────────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!selectedTypeId) {
      showNotification("warning", "Please select an episode type", "", "topRight");
      return;
    }
    setIsSaving(true);
    try {
      await createEpisode({
        patientId,
        episodeTypeId: selectedTypeId,
        startDate,
        assignedClinicUserId: assignedUserId ?? undefined,
        meta,
      });
      showNotification("success", "Episode created", "", "topRight");
      onClose();
      onCreated?.();
    } catch (error: any) {
      showNotification(
        "error",
        "Failed to create episode",
        error?.response?.data?.message || error?.message || "",
        "topRight"
      );
    } finally {
      setIsSaving(false);
    }
  };

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-4">
      <div>
        <div className="text-xs text-gray-500 uppercase tracking-wide font-semibold">Patient</div>
        <div className="text-sm font-semibold text-gray-900">
          {patientName || `Patient #${patientId}`}
        </div>
      </div>

      {/* Treating doctor — universal, shown for all episode types */}
      <div>
        <label className="block text-sm font-medium text-gray-700">
          Treating Doctor
        </label>
        <Select
          style={{ width: "100%" }}
          value={assignedUserId ?? undefined}
          onChange={(val) => setAssignedUserId(val ?? null)}
          allowClear
          showSearch
          filterOption={(input, option) =>
            String(option?.label ?? "").toLowerCase().includes(input.toLowerCase())
          }
          options={doctors.map((d) => ({
            value: d.id,
            label: [d.firstName, d.lastName].filter(Boolean).join(" ") || d.name || `User #${d.id}`,
          }))}
          placeholder="Select treating doctor (optional)"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Episode Type</label>
          <Select
            style={{ width: "100%" }}
            value={selectedTypeId ?? undefined}
            onChange={(value) => setSelectedTypeId(value)}
            options={availableEpisodeTypes.map((type) => ({ value: type.id, label: type.name }))}
            placeholder="Select episode type"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Start Date</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
          />
        </div>
      </div>

      {metaFields.length > 0 && (
        <div className="border-t border-gray-100 pt-3">
          <div className="text-sm font-medium text-gray-700 mb-3">Episode Details</div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-4">
            {metaFields.map((field: any) => renderField(field))}
          </div>
        </div>
      )}

      <div className="flex justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={isSaving}
          onClick={handleSave}
          className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
        >
          {isSaving ? "Creating..." : "Create Episode"}
        </button>
      </div>
    </div>
  );
};

export default CreateEpisodeModal;
